/**
 * Demo-adapter voor Frits (?demo=1, alleen dev-build op localhost).
 *
 * In demo-modus mag er NIETS de deur uit: geen verzoek naar de engine, geen Graph
 * (mailbox doorzoeken, concepten, doorsturen) en geen Microsoft To Do. Daarom
 * lopen álle externe helpers van de mail-assistent via dit bestand: in demo krijgt
 * elke aanroep een neppe OK terug en wordt de echte helper niet aangeroepen.
 * Buiten demo is dit een doorgeefluik naar de echte helpers.
 *
 * Nieuwe externe helper in MailAssistent? Voeg hem hier toe, niet direct importeren
 * (de test in __tests__/demoAdapter.test.js bewaakt dat).
 */
import { authFetch as echteAuthFetch } from '../../utils/authHelpers';
import * as todo from '../../utils/microsoftTodoService';
import * as graph from '../../utils/graphMailSearch';
import { isDemoModus } from './fritsTriage';
import { maakDemoMails, maakDemoReminders } from './__fixtures__/demoMails';

const demoAntwoord = (data, status = 200) => Promise.resolve({ ok: status < 400, status, json: async () => data });

export function demoFetch(url) {
  const u = String(url);
  if (/\/mail-assistant\/items\?status=open/.test(u)) return demoAntwoord({ items: maakDemoMails() });
  if (/\/mail-assistant\/items\?status=/.test(u)) return demoAntwoord({ items: [] });
  if (/\/mail-assistant\/offer-reminders$/.test(u)) return demoAntwoord({ reminders: maakDemoReminders() });
  if (/\/mail-assistant\/run$/.test(u)) return new Promise((r) => { setTimeout(() => r({ ok: true, status: 200, json: async () => ({ processed: 0 }) }), 1200); });
  // Alles wat schrijft (taken, To Do voor collega, signature, toon …): neppe OK.
  return demoAntwoord({ success: true });
}

const DEMO_BODY = 'Demo-modus: de originele mail wordt niet uit je mailbox opgehaald.';

// Neppe antwoorden per helper, in de vorm die de echte helper teruggeeft.
const DEMO = {
  initMicrosoftTodoService: () => undefined,
  createTasksBatch: async (taken = []) => ({
    created: taken.map((t, i) => ({ id: `demo-todo-${i}`, title: t?.name || '' })),
    errors: [], listName: 'Demo',
  }),
  fetchRecentInbox: async () => [],
  fetchRecentSent: async () => [],
  searchMail: async () => [],
  getMailBody: async () => DEMO_BODY,
  createReplyDraft: async () => 'demo-draft',
  deleteDraft: async () => undefined,
  forwardMail: async (_id, _aan, _tekst, { send = false } = {}) => (send ? { sent: true } : { draftId: 'demo-forward', webLink: null }),
};

const kies = (naam, echt) => (...args) => (isDemoModus() ? DEMO[naam](...args) : echt(...args));

export const authFetch = (url, opts) => (isDemoModus() ? demoFetch(url, opts) : echteAuthFetch(url, opts));
export const initMicrosoftTodoService = kies('initMicrosoftTodoService', (...a) => todo.initMicrosoftTodoService(...a));
export const createMicrosoftTodoTasks = kies('createTasksBatch', (...a) => todo.createTasksBatch(...a));
export const fetchRecentInbox = kies('fetchRecentInbox', (...a) => graph.fetchRecentInbox(...a));
export const fetchRecentSent = kies('fetchRecentSent', (...a) => graph.fetchRecentSent(...a));
export const searchMail = kies('searchMail', (...a) => graph.searchMail(...a));
export const getMailBody = kies('getMailBody', (...a) => graph.getMailBody(...a));
export const createReplyDraft = kies('createReplyDraft', (...a) => graph.createReplyDraft(...a));
export const deleteDraft = kies('deleteDraft', (...a) => graph.deleteDraft(...a));
export const forwardMail = kies('forwardMail', (...a) => graph.forwardMail(...a));

/** Namen van alle afgeschermde helpers — voor de test. */
export const AFGESCHERMD = Object.keys(DEMO);
